import React, { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '../../app/appLayout.js';
import { consumeApi } from '../../../api/consumeApi.js';
import { healthApi } from '../../../api/healthApi.js';
import {
	ResponsiveContainer,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	LabelList,
	ReferenceLine,
	Legend
} from 'recharts';

const formatValueLabel = (v) => {
	if (v == null || !Number.isFinite(Number(v))) return '';
	const num = Number(v);
	// Keep labels compact for readability.
	if (Math.abs(num % 1) < 1e-9) return String(Math.trunc(num));
	return num.toFixed(1).replace(/\.0$/, '');
};

const toYmd = (d) => {
	// Format Date to YYYY-MM-DD in local time
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

// Parse a blood-pressure timestamp (comes back as a PHP DateTime object
// {date, timezone} or a string) into a local calendar Date. BP is stored at
// UTC midnight for a day, so we read the YYYY-MM-DD portion to avoid a tz shift.
const parseBpDate = (value) => {
	if (!value && value !== 0) return null;
	if (typeof value === 'number') {
		const d = new Date(value < 1e12 ? value * 1000 : value);
		return isNaN(d.getTime()) ? null : d;
	}
	let str = null;
	if (typeof value === 'object' && value.date) str = String(value.date);
	else if (typeof value === 'string') str = value;
	if (!str) return null;
	const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
	const d = new Date(str);
	return isNaN(d.getTime()) ? null : d;
};

const defaultEnd = new Date();
const defaultStart = new Date(new Date().setDate(defaultEnd.getDate() - 30));

const ViewGraphScreen = () => {
	const [mode, setMode] = useState('nutrients'); // 'nutrients' | 'bp'
	const [nutrient, setNutrient] = useState('saturatedFatG');
	const [bpSeries, setBpSeries] = useState([]);
	const [startDate, setStartDate] = useState(toYmd(defaultStart));
	const [endDate, setEndDate] = useState(toYmd(defaultEnd));
	const [series, setSeries] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [availableIssues, setAvailableIssues] = useState([]);
	const [selectedIssues, setSelectedIssues] = useState([]);
	const [issueData, setIssueData] = useState([]);
	const [loadingIssues, setLoadingIssues] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [showDropdown, setShowDropdown] = useState(false);

	const validRange = useMemo(() => {
		return startDate && endDate && startDate <= endDate;
	}, [startDate, endDate]);

	const referenceLineValue = useMemo(() => {
		const refValues = {
			saturatedFatG: 20,
			fiberG: 28,
			caffeineMg: 35,
			alcoholG: 0,
			sodiumMg: 2300,
		};
		return refValues[nutrient];
	}, [nutrient]);

	// Fetch available issues on mount
	useEffect(() => {
		const fetchAvailableIssues = async () => {
			try {
				const resp = await healthApi.getIssues({ startDate: '1970-01-01', endDate: '2100-12-31' });
				if (resp && resp.status && resp.data && Array.isArray(resp.data.rows)) {
					const names = Array.from(new Set(resp.data.rows.map(r => r.issue || r.name).filter(Boolean)));
					setAvailableIssues(names.sort());
				}
			} catch (e) {
				console.error('Failed to load available issues', e);
			}
		};
		fetchAvailableIssues();
	}, []);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (showDropdown && !event.target.closest('[data-issue-dropdown]')) {
				setShowDropdown(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showDropdown]);

	// Fetch issue data when selected issues change
	useEffect(() => {
		let cancelled = false;

		const fetchIssueData = async () => {
			if (mode !== 'nutrients' || selectedIssues.length === 0) {
				setIssueData([]);
				return;
			}

			setLoadingIssues(true);
			try {
				const issuesParam = selectedIssues.join(',');
				const resp = await healthApi.getIssueOnDay({ issues: issuesParam });
				if (!cancelled) {
					if (resp.status && resp.data && Array.isArray(resp.data.rows)) {
						setIssueData(resp.data.rows);
					} else {
						setIssueData([]);
					}
				}
			} catch (e) {
				if (!cancelled) {
					console.error('Failed to load issue data', e);
					setIssueData([]);
				}
			} finally {
				if (!cancelled) setLoadingIssues(false);
			}
		};

		fetchIssueData();
		return () => {
			cancelled = true;
		};
	}, [mode, selectedIssues]);

	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (mode !== 'nutrients') return;
			if (!validRange) {
				setError('Start date must be before or equal to end date');
				setSeries([]);
				return;
			}

			setLoading(true);
			setError('');
			try {
				const resp = await consumeApi.trackNutrient(startDate, endDate, nutrient);
				if (!cancelled) {
					if (resp.status) {
						// Transform resp.data.rows { 'YYYY-MM-DD': number } -> [{ date, value }]
						const rows = resp?.data?.rows || {};
						const data = Object.keys(rows)
							.sort()
							.map((k) => ({ date: k, value: rows[k] }))
							.filter((p) => p.date && typeof p.value === 'number');
						setSeries(data);
					} else {
						setSeries([]);
						setError(resp.msg || 'Failed to load data');
					}
				}
			} catch (e) {
				if (!cancelled) {
					setError('Network error loading data');
					setSeries([]);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchData();
		return () => {
			cancelled = true;
		};
	}, [mode, nutrient, startDate, endDate, validRange]);

	// Fetch blood pressure when in BP mode. Multiple readings on the same day are
	// averaged into one point so the date axis stays clean.
	useEffect(() => {
		if (mode !== 'bp') return;
		let cancelled = false;

		const fetchBp = async () => {
			if (!validRange) {
				setError('Start date must be before or equal to end date');
				setBpSeries([]);
				return;
			}

			setLoading(true);
			setError('');
			try {
				const resp = await healthApi.getBloodPressure({ startDate, endDate });
				if (cancelled) return;
				if (resp.status && resp.data && Array.isArray(resp.data.rows)) {
					const byDay = {};
					resp.data.rows.forEach((r) => {
						const sys = Number(r.systolic != null ? r.systolic : r.systolic_mm_hg);
						const dia = Number(r.diastolic != null ? r.diastolic : r.diastolic_mm_hg);
						const d = parseBpDate(r.createdAt || r.created_at || r.doneAt || r.done_at || r.date);
						if (!d || !Number.isFinite(sys) || !Number.isFinite(dia)) return;
						const key = toYmd(d);
						if (!byDay[key]) byDay[key] = { sysSum: 0, diaSum: 0, n: 0 };
						byDay[key].sysSum += sys;
						byDay[key].diaSum += dia;
						byDay[key].n += 1;
					});
					const data = Object.keys(byDay)
						.sort()
						.map((k) => ({
							date: k,
							systolic: Math.round(byDay[k].sysSum / byDay[k].n),
							diastolic: Math.round(byDay[k].diaSum / byDay[k].n),
						}));
					setBpSeries(data);
				} else {
					setBpSeries([]);
					setError(resp.msg || 'Failed to load blood pressure');
				}
			} catch (e) {
				if (!cancelled) {
					setError('Network error loading data');
					setBpSeries([]);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchBp();
		return () => {
			cancelled = true;
		};
	}, [mode, startDate, endDate, validRange]);

	const handleIssueToggle = (issue) => {
		setSelectedIssues(prev => {
			if (prev.includes(issue)) {
				return prev.filter(i => i !== issue);
			} else if (prev.length < 3) {
				return [...prev, issue];
			}
			return prev;
		});
	};

	const removeIssue = (issue) => {
		setSelectedIssues(prev => prev.filter(i => i !== issue));
	};

	const filteredIssues = useMemo(() => {
		if (!searchTerm) return availableIssues;
		const term = searchTerm.toLowerCase();
		return availableIssues.filter(issue => issue.toLowerCase().includes(term));
	}, [availableIssues, searchTerm]);

	// Merge issue dates into series data so ReferenceLine can render properly
	// ReferenceLine needs dates to exist in the data array
	const mergedSeries = useMemo(() => {
		const seriesMap = new Map(series.map(d => [d.date, d]));
		const issueDates = new Set();
		
		// Collect all issue dates within range
		issueData.forEach(item => {
			const date = item.date;
			if (date >= startDate && date <= endDate) {
				issueDates.add(date);
			}
		});
		
		// Add issue dates to series if they don't exist
		issueDates.forEach(date => {
			if (!seriesMap.has(date)) {
				seriesMap.set(date, { date, value: null });
			}
		});
		
		return Array.from(seriesMap.values()).sort((a, b) => a.date.localeCompare(b.date));
	}, [series, issueData, startDate, endDate]);

	// Map issue to color based on selection order
	const issueColorMap = useMemo(() => {
		const colors = ['#ff0000', '#00aa00', '#0066ff']; // Red, Green, Blue
		const map = {};
		selectedIssues.forEach((issue, index) => {
			map[issue] = colors[index] || '#666666';
		});
		return map;
	}, [selectedIssues]);

	// Group issue data by date for rendering reference lines (only within date range)
	// Each date can have multiple issues, so we'll render separate lines for each
	const issueLinesByDate = useMemo(() => {
		const lines = [];
		issueData.forEach(item => {
			const date = item.date;
			// Only include dates within the selected range
			if (date >= startDate && date <= endDate) {
				lines.push({
					date,
					issue: item.issue,
					color: issueColorMap[item.issue] || '#666666'
				});
			}
		});
		// Group by date for labels, but keep individual entries for rendering
		return lines;
	}, [issueData, startDate, endDate, issueColorMap]);

	return (
		<AppLayout>
			<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
				<div style={{ display: 'flex', gap: 8 }}>
					<button
						onClick={() => setMode('nutrients')}
						style={{ padding: '6px 14px', fontSize: 14, cursor: 'pointer', borderRadius: 6, border: '1px solid ' + (mode === 'nutrients' ? '#4338ca' : '#ccc'), background: mode === 'nutrients' ? '#4338ca' : '#fff', color: mode === 'nutrients' ? '#fff' : '#333', fontWeight: mode === 'nutrients' ? 700 : 400 }}
					>
						Nutrients
					</button>
					<button
						onClick={() => setMode('bp')}
						style={{ padding: '6px 14px', fontSize: 14, cursor: 'pointer', borderRadius: 6, border: '1px solid ' + (mode === 'bp' ? '#4338ca' : '#ccc'), background: mode === 'bp' ? '#4338ca' : '#fff', color: mode === 'bp' ? '#fff' : '#333', fontWeight: mode === 'bp' ? 700 : 400 }}
					>
						Blood Pressure
					</button>
				</div>

				<h2 style={{ margin: 0 }}>{mode === 'bp' ? 'Track Blood Pressure' : 'Track Nutrient'}</h2>
				<div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
					{mode === 'nutrients' && (
						<label>
							<span style={{ marginRight: 6 }}>Nutrient</span>
							<select value={nutrient} onChange={(e) => setNutrient(e.target.value)}>
								<option value="saturatedFatG">Saturated Fat</option>
								<option value="fiberG">Fiber</option>
								<option value="alcoholG">Alcohol</option>
								<option value="caffeineMg">Caffeine</option>
								<option value="sodiumMg">Sodium</option>
							</select>
						</label>
					)}
					<label>
						<span style={{ marginRight: 6 }}>Start</span>
						<input
							type="date"
							value={startDate}
							max={endDate}
							onChange={(e) => setStartDate(e.target.value)}
						/>
					</label>
					<label>
						<span style={{ marginRight: 6 }}>End</span>
						<input
							type="date"
							value={endDate}
							min={startDate}
							onChange={(e) => setEndDate(e.target.value)}
						/>
					</label>
				</div>

				{mode === 'nutrients' && (<>
				<h2 style={{ margin: 0 }}>Track Health Issue(s)</h2>
				<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
					<div style={{ position: 'relative', minWidth: 250 }} data-issue-dropdown>
						<div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
							<div style={{ position: 'relative' }}>
								<input
									type="text"
									placeholder="Search and select issues (up to 3)..."
									value={searchTerm}
									onChange={(e) => {
										setSearchTerm(e.target.value);
										setShowDropdown(true);
									}}
									onFocus={() => setShowDropdown(true)}
									style={{
										width: '100%',
										padding: '6px 8px',
										border: '1px solid #ccc',
										borderRadius: 4,
										fontSize: 14
									}}
								/>
								{showDropdown && filteredIssues.length > 0 && (
									<div
										style={{
											position: 'absolute',
											top: '100%',
											left: 0,
											right: 0,
											background: '#fff',
											border: '1px solid #ccc',
											borderRadius: 4,
											boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
											maxHeight: 200,
											overflowY: 'auto',
											zIndex: 1000,
											marginTop: 2
										}}
									>
										{filteredIssues.map(issue => {
											const isSelected = selectedIssues.includes(issue);
											const isDisabled = !isSelected && selectedIssues.length >= 3;
											return (
												<div
													key={issue}
													onClick={() => {
														if (!isDisabled) {
															handleIssueToggle(issue);
															setSearchTerm('');
															setShowDropdown(false);
														}
													}}
													style={{
														padding: '8px 12px',
														cursor: isDisabled ? 'not-allowed' : 'pointer',
														background: isSelected ? '#e6f0ff' : isDisabled ? '#f5f5f5' : '#fff',
														color: isDisabled ? '#999' : '#000',
														borderBottom: '1px solid #eee'
													}}
													onMouseEnter={(e) => {
														if (!isDisabled) {
															e.currentTarget.style.background = isSelected ? '#d0e0ff' : '#f0f0f0';
														}
													}}
													onMouseLeave={(e) => {
														e.currentTarget.style.background = isSelected ? '#e6f0ff' : isDisabled ? '#f5f5f5' : '#fff';
													}}
												>
													{issue} {isSelected && '✓'}
												</div>
											);
										})}
									</div>
								)}
							</div>
							{selectedIssues.length > 0 && (
								<div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
									{selectedIssues.map(issue => (
										<span
											key={issue}
											style={{
												display: 'inline-flex',
												alignItems: 'center',
												gap: 4,
												padding: '4px 8px',
												background: '#ffe6e6',
												color: '#c1121f',
												borderRadius: 12,
												fontSize: 12
											}}
										>
											{issue}
											<button
												onClick={() => removeIssue(issue)}
												style={{
													background: 'none',
													border: 'none',
													color: '#c1121f',
													cursor: 'pointer',
													padding: 0,
													marginLeft: 4,
													fontSize: 14,
													lineHeight: 1
												}}
											>
												×
											</button>
										</span>
									))}
								</div>
							)}
						</div>
					</div>
					{selectedIssues.length > 0 && (
						<button
							onClick={() => {
								setSelectedIssues([]);
								setSearchTerm('');
							}}
							style={{
								padding: '6px 12px',
								fontSize: 12,
								cursor: 'pointer',
								background: '#f5f5f5',
								border: '1px solid #ccc',
								borderRadius: 4
							}}
						>
							Clear All
						</button>
					)}
				</div>
				{availableIssues.length === 0 && (
					<div style={{ fontSize: 12, color: '#666' }}>Loading available issues...</div>
				)}
				</>)}

				{error && (
					<div style={{ color: 'crimson' }}>{error}</div>
				)}

				<div style={{ width: '100%', height: 360, border: '1px solid #eee', borderRadius: 8, padding: 8 }}>
					{loading || loadingIssues ? (
						<div>Loading…</div>
					) : mode === 'bp' ? (
						bpSeries.length === 0 ? (
							<div style={{ color: '#666' }}>No blood pressure readings in this range.</div>
						) : (
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={bpSeries} margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									dataKey="date"
									interval={0}
									tick={{ fontSize: 11, angle: -45, textAnchor: 'end', height: 70 }}
								/>
								<YAxis domain={[40, 200]} label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }} />
								<Tooltip />
								<Legend />
								{/* AHA normal thresholds: <120 systolic / <80 diastolic */}
								<ReferenceLine y={120} stroke="#dc2626" strokeDasharray="5 5" label={{ value: 'Systolic 120', position: 'right', fill: '#dc2626', fontSize: 10 }} />
								<ReferenceLine y={80} stroke="#2563eb" strokeDasharray="5 5" label={{ value: 'Diastolic 80', position: 'right', fill: '#2563eb', fontSize: 10 }} />
								<Line type="monotone" dataKey="systolic" name="Systolic" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} connectNulls>
									<LabelList dataKey="systolic" position="top" offset={6} style={{ fontSize: 10, fill: '#dc2626' }} />
								</Line>
								<Line type="monotone" dataKey="diastolic" name="Diastolic" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 6 }} connectNulls>
									<LabelList dataKey="diastolic" position="bottom" offset={6} style={{ fontSize: 10, fill: '#2563eb' }} />
								</Line>
							</LineChart>
						</ResponsiveContainer>
						)
					) : (
						<ResponsiveContainer width="100%" height="100%">
							<LineChart data={mergedSeries} margin={{ top: 16, right: 16, left: 8, bottom: 16 }}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis
									dataKey="date"
									interval={0}
									tick={{ fontSize: 11, angle: -45, textAnchor: 'end', height: 70 }}
								/>
								<YAxis />
								<Tooltip />
								<ReferenceLine y={referenceLineValue} stroke="#ff0000" strokeWidth={2} strokeDasharray="5 5" label={{ value: `Target: ${referenceLineValue}`, position: 'right' }} />
								{issueLinesByDate.map((line, index) => {
									// Render a separate line for each issue, each with its own color
									// If multiple issues on same date, they'll overlay with different colors
									// Calculate offset for labels when multiple issues on same date
									const sameDateIssues = issueLinesByDate.filter(l => l.date === line.date);
									const sameDateIndex = sameDateIssues.findIndex(l => l.issue === line.issue && l.date === line.date);
									const offset = 10 + sameDateIndex * 15; // Offset labels for readability
									
									return (
										<ReferenceLine
											key={`${line.date}-${line.issue}-${index}`}
											x={line.date}
											stroke={line.color}
											strokeWidth={2}
											strokeDasharray="3 3"
											label={{
												value: line.issue,
												position: 'top',
												angle: -90,
												offset: offset,
												fill: line.color,
												fontSize: 10
											}}
										/>
									);
								})}
								<Line
									type="linear"
									dataKey="value"
									stroke="#8884d8"
									strokeWidth={2}
									dot={{ r: 4, stroke: '#8884d8', strokeWidth: 1, fill: '#8884d8' }}
									activeDot={{ r: 6 }}
								>
									<LabelList
										dataKey="value"
										position="top"
										formatter={formatValueLabel}
										offset={6}
										style={{ fontSize: 10 }}
									/>
								</Line>
							</LineChart>
						</ResponsiveContainer>
					)}
				</div>
			</div>
		</AppLayout>
	);
};

export { ViewGraphScreen };
